var _a, _b, _c;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import view from './historyConfigSidebar.view.html';
import './historyConfigSidebar.scss';
import { AiAgentSidebarStateService } from '../../../services/AiAgentSidebarStateService';
import { BlipToastService } from 'modules/shared/BlipToastService';
import { AlertService } from 'modules/shared/AlertService';
import { BuilderFeatures } from '../../../../BuilderFeatures';
let HistoryConfigSidebar = class HistoryConfigSidebar {
    static $inject = ['AiAgentSidebarStateService', 'BlipToastService', 'AlertService', '$timeout', '$scope', '$translate'];
    constructor(AiAgentSidebarStateService, BlipToastService, AlertService, $timeout, $scope, $translate) {
        //#region States
        this.historyLength = 50;
        this.historyOrder = 'desc';
        this.initialLength = 50;
        this.initialOrder = 'desc';
        this.dataLoaded = false;
        this.wasOpen = false;
        this.isEditing = false;
        this.targetPromptId = null;
        this.maxHistoryLength = 100; // Maximum value allowed from agent configuration
        this.previousMaxHistoryLength = 100; // Track previous max to detect changes
        this.isLengthValid = true; // Track if the current length value is valid
        this.isFeatureEnabled = false; // Feature toggle state
        this.onLengthChange = (event) => {
            const value = parseInt(event.detail.value, 10);
            // Always update the value for display purposes
            this.historyLength = value;
            this.isEditing = true;
            // Validate against maxHistoryLength from agent configuration
            this.isLengthValid = !isNaN(value) && value >= 1 && value <= this.maxHistoryLength;
        };
        this.onOrderChange = (event) => {
            const radioElement = event.target;
            const value = radioElement?.getAttribute('value') || radioElement?.value;
            const isChecked = event.detail?.checked;
            if (isChecked && (value === 'asc' || value === 'desc')) {
                this.historyOrder = value;
                this.isEditing = true;
                this.$timeout(() => this.updateRadioButtons());
            }
        };
        this.setOrder = (order) => {
            this.historyOrder = order;
        };
        this.AiAgentSidebarStateService = AiAgentSidebarStateService;
        this.BlipToastService = BlipToastService;
        this.AlertService = AlertService;
        this.$timeout = $timeout;
        this.$scope = $scope;
        this.$translate = $translate;
    }
    //#region Lifecycle Hooks
    async $onInit() {
        // Check if feature is enabled
        this.isFeatureEnabled = await BuilderFeatures.isAiAgentSharedMemoryManipulationEnabled();
        this.$scope.$watch(() => this.isOpen(), (newValue, oldValue) => {
            this.handleSidebarStateChange(newValue, oldValue);
        });
    }
    /**
     * Handle sidebar open/close state changes
     */
    handleSidebarStateChange(isCurrentlyOpen, wasOpen) {
        if (this.isSidebarOpening(isCurrentlyOpen, wasOpen)) {
            this.handleSidebarOpened();
        }
        else if (this.isSidebarClosing(isCurrentlyOpen, wasOpen)) {
            this.handleSidebarClosed();
        }
    }
    /**
     * Check if sidebar is transitioning from closed to open
     */
    isSidebarOpening(isCurrentlyOpen, wasOpen) {
        return isCurrentlyOpen && !wasOpen;
    }
    /**
     * Check if sidebar is transitioning from open to closed
     */
    isSidebarClosing(isCurrentlyOpen, wasOpen) {
        return !isCurrentlyOpen && wasOpen;
    }
    /**
     * Handle sidebar opened event
     */
    handleSidebarOpened() {
        this.isEditing = false;
        this.loadConfigData();
    }
    /**
     * Handle sidebar closed event
     */
    handleSidebarClosed() {
        this.dataLoaded = false;
        this.isEditing = false;
    }
    $doCheck() {
        const isCurrentlyOpen = this.isOpen();
        const sidebarData = this.AiAgentSidebarStateService.getData();
        const currentMaxHistoryLength = sidebarData?.maxHistoryLength || 100;
        // Reload data when sidebar opens
        if (isCurrentlyOpen && !this.wasOpen && !this.isEditing) {
            this.loadConfigData();
        }
        // Reload data when maxHistoryLength changes (agent was changed)
        if (isCurrentlyOpen && currentMaxHistoryLength !== this.previousMaxHistoryLength) {
            this.previousMaxHistoryLength = currentMaxHistoryLength;
            this.loadConfigData();
        }
        this.wasOpen = isCurrentlyOpen;
    }
    loadConfigData() {
        const sidebarData = this.AiAgentSidebarStateService.getData();
        if (sidebarData?.currentConfig) {
            // Existing config - update with new maxHistoryLength
            this.historyOrder = sidebarData.currentConfig.order;
            // Set max history length from agent configuration
            if (sidebarData.maxHistoryLength) {
                this.maxHistoryLength = sidebarData.maxHistoryLength;
                this.previousMaxHistoryLength = sidebarData.maxHistoryLength;
            }
            else {
                this.maxHistoryLength = 100; // Default if not provided
                this.previousMaxHistoryLength = 100;
            }
            // Adjust length if it exceeds new max (when changing agents)
            const requestedLength = sidebarData.currentConfig.length;
            if (requestedLength > this.maxHistoryLength) {
                this.historyLength = this.maxHistoryLength; // Cap at new max
            }
            else {
                this.historyLength = requestedLength;
            }
            this.initialLength = this.historyLength;
            this.initialOrder = this.historyOrder;
            this.dataLoaded = true;
            this.targetPromptId = sidebarData.promptId || null;
            // Validate the loaded length value
            this.isLengthValid = this.historyLength >= 1 && this.historyLength <= this.maxHistoryLength;
            this.$timeout(() => this.updateRadioButtons());
        }
        else if (sidebarData?.maxHistoryLength) {
            // No existing config (new creation) - use maxHistoryLength as default
            this.maxHistoryLength = sidebarData.maxHistoryLength;
            this.previousMaxHistoryLength = sidebarData.maxHistoryLength;
            this.historyLength = this.maxHistoryLength; // Set to max by default
            this.historyOrder = 'desc'; // Default order
            this.initialLength = this.historyLength;
            this.initialOrder = this.historyOrder;
            this.dataLoaded = true;
            this.targetPromptId = sidebarData.promptId || null;
            this.isLengthValid = true; // Max value is always valid
            this.$timeout(() => this.updateRadioButtons());
        }
    }
    /**
     * Synchronize radio button state with model.
     * Required because BDS web components don't support AngularJS bindings.
     */
    updateRadioButtons() {
        const radios = document.querySelectorAll('bds-radio[name="historyOrder"]');
        radios.forEach((radio) => {
            radio.checked = radio.getAttribute('value') === this.historyOrder;
        });
    }
    //#endregion
    //#region Methods
    isOpen() {
        // If feature is disabled, do not open the sidebar
        if (!this.isFeatureEnabled) {
            return false;
        }
        return this.AiAgentSidebarStateService.isOpen('historyConfig');
    }
    closeSidebar() {
        this.AiAgentSidebarStateService.close();
    }
    hasChanges() {
        return this.historyLength !== this.initialLength ||
            this.historyOrder !== this.initialOrder;
    }
    canSave() {
        return this.isLengthValid && this.hasChanges();
    }
    async handleCancel() {
        if (this.hasChanges()) {
            await this.AlertService.show({
                title: this.$translate.instant('historyConfigSidebar.alerts.discardChanges.title'),
                body: this.$translate.instant('historyConfigSidebar.alerts.discardChanges.body'),
                variant: 'warning',
                icon: 'warning',
                buttons: {
                    cancel: this.$translate.instant('historyConfigSidebar.alerts.discardChanges.cancel'),
                    confirm: this.$translate.instant('historyConfigSidebar.alerts.discardChanges.confirm'),
                },
                callback: {
                    cancelButtonAction: () => { },
                    confirmButtonAction: async () => { this.confirmDiscard(); },
                },
            });
        }
        else {
            this.closeSidebar();
        }
    }
    confirmDiscard() {
        this.historyLength = this.initialLength;
        this.historyOrder = this.initialOrder;
        this.closeSidebar();
    }
    saveConfig() {
        try {
            const currentData = this.AiAgentSidebarStateService.getData() || {};
            const updatedData = {
                ...currentData,
                currentConfig: {
                    length: this.historyLength,
                    order: this.historyOrder,
                },
                updatedConfig: {
                    length: this.historyLength,
                    order: this.historyOrder,
                    targetPromptId: this.targetPromptId,
                },
            };
            // @ts-ignore - Direct state update required to avoid sidebar reopen cycle
            this.AiAgentSidebarStateService['state'].data = updatedData;
            this.$timeout(() => {
                const data = this.AiAgentSidebarStateService.getData();
                if (data?.['updatedConfig']) {
                    // @ts-ignore
                    this.AiAgentSidebarStateService['state'].data = {
                        ...data,
                        updatedConfig: null,
                    };
                }
            }, 10);
            this.$timeout(() => this.closeSidebar(), 50);
            this.$timeout(() => {
                this.BlipToastService.show('success', {
                    msg: this.$translate.instant('historyConfigSidebar.toasts.success.message'),
                    title: this.$translate.instant('historyConfigSidebar.toasts.success.title'),
                });
            }, 100);
            this.initialLength = this.historyLength;
            this.initialOrder = this.historyOrder;
            this.isEditing = false;
        }
        catch (error) {
            this.isEditing = false;
            this.BlipToastService.show('danger', {
                msg: this.$translate.instant('historyConfigSidebar.toasts.error.message'),
                title: this.$translate.instant('historyConfigSidebar.toasts.error.title'),
            });
        }
    }
};
//#endregion
HistoryConfigSidebar.$inject = ['AiAgentSidebarStateService', 'BlipToastService', 'AlertService', '$timeout', '$scope', '$translate'];
HistoryConfigSidebar = __decorate([
    Component({
        controllerAs: '$ctrl',
        template: view,
        bindings: {},
    }),
    __metadata("design:paramtypes", [typeof (_a = typeof AiAgentSidebarStateService !== "undefined" && AiAgentSidebarStateService) === "function" ? _a : Object, typeof (_b = typeof BlipToastService !== "undefined" && BlipToastService) === "function" ? _b : Object, typeof (_c = typeof AlertService !== "undefined" && AlertService) === "function" ? _c : Object, Object, Object, Object])
], HistoryConfigSidebar);
export { HistoryConfigSidebar };
